/**
 * Fingerprint Service - ATTENDANCE ONLY
 * Handles fingerprint capture, enrollment, and verification for Mantra MFS110
 * 
 * IMPORTANT: This service is for EMPLOYEE ATTENDANCE purposes only.
 * It uses a LOCAL fingerprint scanner (Mantra MFS110) connected to the system.
 * This is NOT for Aadhaar verification or identity authentication.
 * 
 * Device: Mantra MFS110 L1 Fingerprint Scanner
 * Specifications: https://www.mantratec.com/products/Fingerprint-Sensors/L1-Fingerprint-Scanner-MFS110
 * - USB 2.0 interface
 * - 500 DPI optical sensor
 * - Auto finger detection
 * - MINEX Compliance Extractor
 * - Supports Windows and Android
 * 
 * The RD Service must be installed locally on the machine running this application.
 * Default port: 8080 (configurable via VITE_RD_SERVICE_URL environment variable)
 */

import apiClient from '../lib/apiClient';

// Check if WebUSB is available (for direct browser access)
const isWebUSBAvailable = () => {
  return 'usb' in navigator;
};

// Check if device is connected via USB
const checkDeviceConnection = async () => {
  try {
    // Mantra MFS110 USB Vendor ID and Product ID
    // These need to be obtained from Mantra SDK documentation
    const device = await navigator.usb.requestDevice({
      filters: [
        { vendorId: 0x0C30 }, // Mantra vendor ID (example - verify with actual device)
        { classCode: 0xFF     // Vendor specific class
        }
      ]
    });
    return { connected: true, device };
  } catch (error) {
    return { connected: false, error: error.message };
  }
};

// Capture fingerprint using Mantra SDK
// Note: RD Service appears to only handle device discovery, not capture
// Priority: Mantra SDK > WebUSB > Bridge Service > RD Service (for discovery only)
const captureFingerprint = async () => {
  console.log('[captureFingerprint] Starting fingerprint capture...');
  console.log('[captureFingerprint] Available methods:', {
    sdk: typeof window.MantraSDK !== 'undefined',
    webusb: isWebUSBAvailable(),
    rdservice: true // Always available via proxy
  });

  // Priority 1: Try Mantra SDK directly (most reliable)
  // RD Service discovery is optional - SDK can work without it
  if (typeof window.MantraSDK !== 'undefined' || typeof document !== 'undefined' && document.ScanFinger) {
    console.log('[captureFingerprint] Attempting capture via Mantra SDK...');
    return new Promise((resolve, reject) => {
      try {
        // Check for modern callback-based API (MFS110 Web SDK)
        const sdkOptions = {
          onSuccess: (result) => {
            console.log('[captureFingerprint] ✅ SDK capture successful (callback)');
            // Handle different response formats
            const template = result.template || result.data || result.pidData || result.FMRValue || result;
            const quality = result.quality || 100;
            
            // Validate template length (similar to MFS100 example)
            if (typeof template === 'string' && template.length <= 100) {
              reject({
                success: false,
                error: 'Capture failed, template too short. Please try again.',
                method: 'sdk'
              });
              return;
            }
            
            resolve({
              success: true,
              template: template,
              quality: quality,
              method: 'sdk',
              raw: result
            });
          },
          onError: (error) => {
            console.error('[captureFingerprint] ❌ SDK capture failed:', error);
            reject({
              success: false,
              error: error.message || error.toString() || 'SDK capture failed',
              method: 'sdk'
            });
          },
          timeout: 15000 // 15 seconds timeout
        };

        // Try modern callback-based API first (MFS110 Web SDK)
        if (typeof window.MantraSDK !== 'undefined') {
          if (typeof window.MantraSDK.CaptureFingerprint === 'function') {
            console.log('[captureFingerprint] Using MantraSDK.CaptureFingerprint()');
            window.MantraSDK.CaptureFingerprint(sdkOptions);
            return;
          } else if (typeof window.MantraSDK.captureFingerprint === 'function') {
            console.log('[captureFingerprint] Using MantraSDK.captureFingerprint()');
            window.MantraSDK.captureFingerprint(sdkOptions);
            return;
          } else if (typeof window.MantraSDK.Capture === 'function') {
            console.log('[captureFingerprint] Using MantraSDK.Capture()');
            window.MantraSDK.Capture(sdkOptions);
            return;
          } else {
            console.warn('[captureFingerprint] MantraSDK object found but CaptureFingerprint method not found');
            console.warn('[captureFingerprint] Available methods:', Object.keys(window.MantraSDK).join(', '));
          }
        }

        // Try Java applet-style API (legacy MFS100, but might work for MFS110)
        // Note: Java applets are deprecated but some implementations might emulate this
        if (typeof document !== 'undefined' && document.ScanFinger) {
          console.log('[captureFingerprint] Using document.ScanFinger.CaptureFinger() (applet-style API)');
          try {
            // Initialize if needed
            if (typeof document.ScanFinger.Init === 'function') {
              document.ScanFinger.Init();
            }

            // Capture fingerprint (synchronous call like MFS100 example)
            document.ScanFinger.CaptureFinger();
            
            // Get result from property (similar to MFS100 FMRValue)
            const template = document.ScanFinger.FMRValue || 
                           document.ScanFinger.FMR || 
                           document.ScanFinger.Template ||
                           document.ScanFinger.Value;
            
            if (!template || (typeof template === 'string' && template.length <= 100)) {
              reject({
                success: false,
                error: 'Capture failed, template too short or invalid. Please try again.',
                method: 'sdk'
              });
              return;
            }

            console.log('[captureFingerprint] ✅ SDK capture successful (property-based)');
            resolve({
              success: true,
              template: template,
              quality: document.ScanFinger.Quality || 100,
              method: 'sdk'
            });
            return;
          } catch (appletError) {
            console.error('[captureFingerprint] Applet-style API failed:', appletError);
            // Continue to throw error and try next method
          }
        }

        // If we get here, SDK exists but no valid capture method found
        throw new Error('MantraSDK found but no valid capture method available. ' +
                       'Available SDK methods: ' + 
                       (window.MantraSDK ? Object.keys(window.MantraSDK).join(', ') : 'none') +
                       '. Check SDK documentation for correct API usage.');
      } catch (error) {
        console.error('[captureFingerprint] SDK initialization error:', error);
        reject({
          success: false,
          error: error.message || 'SDK error',
          method: 'sdk'
        });
      }
    }).catch((sdkError) => {
      console.warn('[captureFingerprint] SDK failed, trying other methods...', sdkError);
      // Continue to next method
      throw sdkError;
    });
  }
  
  // Priority 2: Try WebUSB (if browser supports it)
  if (isWebUSBAvailable()) {
    console.log('[captureFingerprint] Attempting capture via WebUSB...');
    try {
      return await captureViaWebUSB();
    } catch (webusbError) {
      console.warn('[captureFingerprint] WebUSB failed, trying next method...', webusbError);
      // Continue to next method
    }
  }
  
  // Priority 3: Try Bridge Service
  console.log('[captureFingerprint] Attempting capture via Bridge Service...');
  try {
    return await captureViaBridgeService();
  } catch (bridgeError) {
    console.warn('[captureFingerprint] Bridge Service failed, trying RD Service...', bridgeError);
    // Continue to RD Service (last resort)
  }
  
  // Priority 4: Try RD Service (though it likely doesn't support capture)
  // This is last because all attempts have returned 500 errors
  console.log('[captureFingerprint] Attempting capture via RD Service (may not be supported)...');
  try {
    return await captureViaRDService('/rdservice');
  } catch (rdServiceError) {
    console.error('[captureFingerprint] All capture methods failed');
    
    // Provide detailed error message based on what's available
    const availableMethods = [];
    if (typeof window !== 'undefined' && typeof window.MantraSDK !== 'undefined') {
      availableMethods.push('SDK (loaded)');
    } else {
      availableMethods.push('SDK (NOT loaded - check if SDK loader is enabled in index.html)');
    }
    
    if (isWebUSBAvailable()) {
      availableMethods.push('WebUSB (available)');
    } else {
      availableMethods.push('WebUSB (not available - requires Chrome/Edge)');
    }
    
    availableMethods.push('Bridge Service (tried)');
    availableMethods.push('RD Service (tried - only handles discovery, not capture)');
    
    const errorMessage = 'Fingerprint capture failed. All methods attempted:\n' +
                         availableMethods.map(m => `  - ${m}`).join('\n') +
                         '\n\nTo enable fingerprint capture:\n' +
                         '1. Install Mantra Web SDK for MFS110\n' +
                         '2. Uncomment SDK loader in index.html: <script src="/mantra-sdk-loader.js"></script>\n' +
                         '3. Configure SDK path in public/mantra-sdk-loader.js\n' +
                         '4. Verify SDK loads: Open browser console and type: console.log(window.MantraSDK)\n' +
                         '5. Ensure fingerprint device is connected and drivers are installed\n' +
                         '\nNote: RD Service is for device discovery only, not capture. You need the Mantra SDK for actual fingerprint capture.';
    
    throw {
      success: false,
      error: errorMessage,
      methodsTried: ['sdk', 'webusb', 'bridge', 'rdservice'],
      sdkAvailable: typeof window !== 'undefined' && typeof window.MantraSDK !== 'undefined',
      webusbAvailable: isWebUSBAvailable()
    };
  }
};

// Capture via WebUSB API (if browser supports it)
const captureViaWebUSB = async () => {
  try {
    const device = await navigator.usb.requestDevice({
      filters: [{ vendorId: 0x0C30 }] // Mantra vendor ID
    });
    
    await device.open();
    await device.selectConfiguration(1);
    await device.claimInterface(0);
    
    // Send capture command (specific to Mantra MFS110 protocol)
    // This needs to be implemented based on Mantra SDK documentation
    const result = await device.transferIn(1, 512);
    
    await device.close();
    
    return {
      success: true,
      template: result.data,
      quality: 100
    };
  } catch (error) {
    throw {
      success: false,
      error: 'WebUSB capture failed: ' + error.message
    };
  }
};

// Capture via bridge service (local service running on user's machine)
// NOTE: Uses proxy to avoid CORS issues - does NOT use direct connections
const captureViaBridgeService = async () => {
  try {
    // ALWAYS use proxy to avoid CORS - never use direct localhost connections
    const proxyUrl = '/rdservice';
    console.log('[captureViaBridgeService] Using proxy for bridge service:', proxyUrl);
    
    const response = await fetch(`${proxyUrl}/capture`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        device: 'MFS110',
        timeout: 10000
      }),
      mode: 'cors',
      credentials: 'omit'
    });
    
    if (!response.ok) {
      throw new Error(`Bridge service error: ${response.status}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      template: data.template,
      quality: data.quality || 100,
      method: 'bridge',
      url: proxyUrl
    };
  } catch (error) {
    throw {
      success: false,
      error: 'Bridge service capture failed via proxy. Please ensure:\n' +
             '1. RD Service is running on localhost:8080\n' +
             '2. Vite dev server is running (proxy configured)\n' +
             '3. Check browser console for detailed error messages'
    };
  }
};

// Enroll fingerprint for an employee
const enrollFingerprint = async (employeeId, template, hand = 'left') => {
  try {
    const result = await apiClient.post('/fingerprint/enroll', {
      employee_id: employeeId,
      template: template,
      hand: hand
    });
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to enroll fingerprint' };
  }
};

// Verify fingerprint against stored template
const verifyFingerprint = async (employeeId, capturedTemplate) => {
  try {
    const result = await apiClient.post('/fingerprint/verify', {
      employee_id: employeeId,
      template: capturedTemplate
    });
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to verify fingerprint' };
  }
};

// Check if employee has enrolled fingerprint
const checkEnrollment = async (employeeId) => {
  try {
    console.log('[fingerprintService] Checking enrollment for employee:', employeeId);
    // Use params object for apiClient.get
    const result = await apiClient.get('/fingerprint/check', { employee_id: employeeId });
    console.log('[fingerprintService] Enrollment check result:', result);
    console.log('[fingerprintService] Result type:', typeof result);
    console.log('[fingerprintService] Result keys:', result ? Object.keys(result) : 'null');
    return result;
  } catch (error) {
    console.error('[fingerprintService] Enrollment check error:', error);
    console.error('[fingerprintService] Error details:', {
      message: error.message,
      stack: error.stack
    });
    return { success: false, error: error.message || 'Failed to check enrollment' };
  }
};

// Check RD Service availability
// NOTE: This is for LOCAL fingerprint scanner (Mantra MFS110) for ATTENDANCE purposes only
// NOT for Aadhaar or identity verification
const checkRDService = async () => {
  // Always try proxy first (avoids CORS issues)
  // Proxy is configured in vite.config.mjs to route /rdservice/* to local RD Service
  const proxyUrl = '/rdservice';
  
  // RD Service URLs - LOCAL INSTALLATION ONLY (for attendance)
  // Priority: Proxy first, then local direct connections
  const rdServiceUrls = [
    // Try proxy first (recommended - avoids CORS)
    proxyUrl,
    // Then try local direct URLs (may fail due to CORS, but useful for testing)
    import.meta.env.VITE_RD_SERVICE_URL,
    'http://localhost:8080',
    'http://127.0.0.1:8080',
    'http://localhost:9090',
    'http://127.0.0.1:9090'
  ].filter(Boolean); // Remove undefined values
  
  console.log('[checkRDService] Checking LOCAL RD Service for attendance (not Aadhaar)');

  console.log('Checking RD Service at URLs (proxy first):', rdServiceUrls);

  for (const url of rdServiceUrls) {
    try {
      // First, try a simple GET request to verify service is reachable (like the test does)
      try {
        console.log(`[checkRDService] Testing basic connection to: ${url}`);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        const getResponse = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        console.log(`[checkRDService] GET response status: ${getResponse.status} for ${url}`);
        
        if (getResponse.status >= 200 && getResponse.status < 500) {
          console.log(`[checkRDService] ✅ Basic connection successful to ${url}, status: ${getResponse.status}`);
          // Service is reachable! Return immediately
          return {
            success: true,
            available: true,
            url: url,
            endpoint: '/',
            data: { message: 'RD Service is reachable' },
            status: getResponse.status,
            method: 'GET'
          };
        } else {
          console.log(`[checkRDService] GET returned status ${getResponse.status}, continuing to POST endpoints...`);
        }
      } catch (getError) {
        console.log(`[checkRDService] GET request failed for ${url}:`, getError.name, getError.message);
        // Continue to try POST endpoints
      }
      
      // Try to discover device via RD Service POST endpoints
      // RD Service might use different endpoints, try common ones
      const endpoints = ['/discover', '/discoverAVDM', '/api/discover', '/rdservice/discover'];
      
      for (const endpoint of endpoints) {
        try {
          const fullUrl = `${url}${endpoint}`;
          console.log(`Trying RD Service POST: ${fullUrl}`);
          
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          const response = await fetch(fullUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({}),
            signal: controller.signal,
            mode: 'cors',
            credentials: 'omit'
          });

          clearTimeout(timeoutId);

          console.log(`Response from ${fullUrl}:`, response.status, response.statusText);

          // Accept any response status (even errors) as long as we got a response
          // This means the service is reachable
          if (response.status >= 200 && response.status < 600) {
            let data;
            try {
              const text = await response.text();
              console.log(`Response text (first 200 chars):`, text.substring(0, 200));
              if (text && text.trim()) {
                try {
                  data = JSON.parse(text);
                } catch (parseError) {
                  // Not JSON, but we got a response
                  data = { message: 'RD Service discovered', rawResponse: text.substring(0, 100) };
                }
              } else {
                data = { message: 'RD Service discovered (empty response)' };
              }
            } catch (e) {
              // If we can't read response, still consider it successful if status is OK
              console.log('Could not read response, but status is:', response.status);
              data = { message: 'RD Service discovered', status: response.status };
            }
            
            // Consider it available if we got any response (even 404 means service is reachable)
            console.log('RD Service found at:', fullUrl, 'Status:', response.status);
            return {
              success: true,
              available: true,
              url: url,
              endpoint: endpoint,
              data: data,
              status: response.status,
              method: 'POST'
            };
          }
        } catch (fetchError) {
          // Log the error for debugging
          console.log(`Failed to connect to ${url}${endpoint}:`, fetchError.name, fetchError.message);
          // If it's an abort error (timeout), try next endpoint
          if (fetchError.name === 'AbortError') {
            console.log('Request timed out, trying next endpoint...');
          }
          // Try next endpoint
          continue;
        }
      }
    } catch (error) {
      // Try next URL
      console.log(`RD Service check failed for ${url}:`, error.message);
      continue;
    }
  }

  console.warn('[checkRDService] ❌ RD Service not found at any URL');
  console.warn('[checkRDService] Tried URLs:', rdServiceUrls);
  return {
    success: false,
    available: false,
    error: 'RD Service not found. Please ensure RD Service is running and accessible. Check browser console for details.'
  };
};

// Capture fingerprint via RD Service
// IMPORTANT: Only uses proxy to avoid CORS issues - does NOT try direct connections
const captureViaRDService = async (rdServiceUrl = null) => {
  // Always use proxy - never try direct connections (they will fail due to CORS)
  const proxyUrl = '/rdservice';
  
  // First, check what the RD Service root endpoint returns (might give us clues about available endpoints)
  console.log('[captureViaRDService] Step 1: Checking RD Service root endpoint...');
  try {
    const rootResponse = await fetch(`${proxyUrl}/`, {
      method: 'GET',
      headers: {
        'Accept': 'text/html,application/json,*/*'
      },
      mode: 'cors',
      credentials: 'omit'
    });
    const rootText = await rootResponse.text();
    console.log('[captureViaRDService] Root endpoint response (first 500 chars):', rootText.substring(0, 500));
  } catch (rootError) {
    console.warn('[captureViaRDService] Root endpoint check failed:', rootError.message);
  }
  
  // Try to discover/initialize the device (some RD Services require this)
  console.log('[captureViaRDService] Step 2: Attempting to discover device...');
  try {
    const discoverEndpoints = ['/discover', '/discoverAVDM', '/api/discover', '/RDSERVICE/discover'];
    for (const discoverEndpoint of discoverEndpoints) {
      try {
        console.log(`[captureViaRDService] Trying discover endpoint: ${discoverEndpoint}`);
        // Try POST first
        let discoverResponse = await fetch(`${proxyUrl}${discoverEndpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({}),
          mode: 'cors',
          credentials: 'omit'
        });
        console.log(`[captureViaRDService] Discover ${discoverEndpoint} (POST) response status:`, discoverResponse.status);
        
        if (!discoverResponse.ok && discoverResponse.status !== 500) {
          // Try GET if POST doesn't work
          discoverResponse = await fetch(`${proxyUrl}${discoverEndpoint}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json'
            },
            mode: 'cors',
            credentials: 'omit'
          });
          console.log(`[captureViaRDService] Discover ${discoverEndpoint} (GET) response status:`, discoverResponse.status);
        }
        
        if (discoverResponse.ok) {
          const discoverData = await discoverResponse.text();
          console.log('[captureViaRDService] ✅ Device discovery successful:', discoverData.substring(0, 200));
          break; // Success, move to capture
        } else {
          const errorText = await discoverResponse.text();
          console.log(`[captureViaRDService] Discover ${discoverEndpoint} response:`, errorText.substring(0, 200));
        }
      } catch (discoverError) {
        console.warn(`[captureViaRDService] Discover ${discoverEndpoint} failed:`, discoverError.message);
      }
    }
  } catch (discoverError) {
    console.warn('[captureViaRDService] Device discovery step failed (may not be required):', discoverError.message);
  }
  
  // Try different endpoint formats that Mantra RD Service might support
  // NOTE: Do NOT include '/rdservice' prefix - proxyUrl already has it
  const endpoints = [
    '/capture',
    '/api/capture',
    '/Capture',
    '/rd/capture',
    '/RDSERVICE/capture',
    '/RDSERVICE/api/capture'
  ];
  
  // Try different request formats
  // IMPORTANT: Mantra RD Service might expect XML or form-data, not JSON
  const requestFormats = [
    // Format 1: Standard RD Service JSON format
    {
      timeout: 10000,
      pidVer: '2.0',
      fingerCount: 1,
      fingerType: 'FMR',
      dataType: 'X'
    },
    // Format 2: Simplified format
    {
      timeout: 10000
    },
    // Format 3: With device specification
    {
      device: 'MFS110',
      timeout: 10000
    },
    // Format 4: Minimal format
    {},
    // Format 5: With RDSERVICE prefix in data
    {
      RDSERVICE: true,
      timeout: 10000
    }
  ];
  
  console.log('[captureViaRDService] Step 3: Attempting capture via proxy:', proxyUrl);
  console.log('[captureViaRDService] NOTE: Using proxy only - direct connections disabled to avoid CORS');
  
  // Try proxy endpoints with different formats
  for (const endpoint of endpoints) {
    for (const requestFormat of requestFormats) {
      try {
        const fullUrl = `${proxyUrl}${endpoint}`;
        console.log('[captureViaRDService] Trying endpoint:', fullUrl, 'with format:', JSON.stringify(requestFormat));
        
        // Try both JSON and form-data formats
        const contentTypes = [
          { 'Content-Type': 'application/json', body: JSON.stringify(requestFormat) },
          { 'Content-Type': 'application/x-www-form-urlencoded', body: new URLSearchParams(requestFormat).toString() },
          { 'Content-Type': 'application/xml', body: null } // Skip XML for now, complex
        ];
        
        for (const contentTypeConfig of contentTypes) {
          try {
            const headers = {
              'Accept': 'application/json,text/plain,*/*',
              ...contentTypeConfig
            };
            delete headers.body; // Remove body from headers
            
            console.log(`[captureViaRDService] Trying ${contentTypeConfig['Content-Type']}...`);
            
            const response = await fetch(fullUrl, {
              method: 'POST',
              headers: headers,
              body: contentTypeConfig.body || undefined,
              mode: 'cors',
              credentials: 'omit'
            });

            console.log(`[captureViaRDService] Response status: ${response.status} for ${contentTypeConfig['Content-Type']}`);
            
            // Get response text first to see what we're getting
            const responseText = await response.text();
            console.log(`[captureViaRDService] Response body (raw, first 200 chars):`, responseText.substring(0, 200));
            
            if (response.ok) {
              let data;
              try {
                data = JSON.parse(responseText);
              } catch (parseError) {
                console.warn('[captureViaRDService] Response is not JSON, treating as raw template data');
                // If it's not JSON, maybe it's the template data directly
                data = { template: responseText, pidData: responseText, data: responseText };
              }
              
              console.log('[captureViaRDService] ✅ Capture successful via proxy with', contentTypeConfig['Content-Type']);
              return {
                success: true,
                data: {
                  biometricData: data.template || data.pidData || data.data || responseText,
                  quality: data.quality || 100
                },
                template: data.template || data.pidData || data.data || responseText,
                quality: data.quality || 100,
                method: 'rdservice',
                url: fullUrl,
                contentType: contentTypeConfig['Content-Type']
              };
            } else {
              // If response is not OK, log but continue trying other content types
              if (response.status !== 500 || responseText) {
                console.log(`[captureViaRDService] ${contentTypeConfig['Content-Type']} returned ${response.status}:`, responseText.substring(0, 200));
              }
              // Continue to next content type
              continue;
            }
          } catch (contentTypeError) {
            console.warn(`[captureViaRDService] ${contentTypeConfig['Content-Type']} failed:`, contentTypeError.message);
            // Continue to next content type
            continue;
          }
        }
        
        // If we get here, all content types failed for this endpoint/format combo
        console.warn(`[captureViaRDService] All content types failed for ${endpoint} with format ${JSON.stringify(requestFormat)}`);
        // Try next format/endpoint combination
        continue;
      } catch (endpointError) {
        console.error(`[captureViaRDService] ❌ Endpoint ${endpoint} with format ${JSON.stringify(requestFormat)} failed:`, endpointError.message);
        // Check if it's a CORS/network error
        if (endpointError.message.includes('CORS') || 
            endpointError.message.includes('NetworkError') || 
            endpointError.message.includes('Failed to fetch') ||
            endpointError.name === 'TypeError') {
          console.error('[captureViaRDService] ⚠️ Network/CORS error detected.');
          console.error('[captureViaRDService] ⚠️ This suggests the Vite proxy may not be working. Check vite.config.mjs');
        }
        // Try next format/endpoint combination
        continue;
      }
    }
  }

  // If all proxy endpoints fail, throw error (don't try direct connections)
  throw {
    success: false,
    error: 'RD Service capture failed via proxy. Please ensure:\n' +
           '1. RD Service is running on localhost:8080\n' +
           '2. Vite dev server is running (proxy configured in vite.config.mjs)\n' +
           '3. Fingerprint device is connected and ready\n' +
           '4. Check browser console for detailed error messages'
  };
};

// Get device status
const getDeviceStatus = async () => {
  try {
    console.log('[getDeviceStatus] Getting device status...');
    
    // First, check RD Service (most common for web apps)
    const rdServiceStatus = await checkRDService();
    console.log('[getDeviceStatus] RD Service status result:', rdServiceStatus);
    console.log('[getDeviceStatus] rdServiceStatus.available:', rdServiceStatus?.available);
    console.log('[getDeviceStatus] rdServiceStatus.success:', rdServiceStatus?.success);
    
    if (rdServiceStatus && (rdServiceStatus.available === true || rdServiceStatus.success === true)) {
      console.log('[getDeviceStatus] ✅ RD Service is available!');
      return {
        success: true,
        available: true,
        method: 'rdservice',
        message: `RD Service discovered successfully at ${rdServiceStatus.url || '/rdservice'}`,
        url: rdServiceStatus.url || '/rdservice'
      };
    } else {
      console.log('[getDeviceStatus] ⚠️ RD Service check returned:', rdServiceStatus);
    }

    // Fallback to Mantra SDK
    if (typeof window.MantraSDK !== 'undefined') {
      return {
        success: true,
        available: true,
        method: 'sdk',
        message: 'Mantra SDK available'
      };
    }

    // Fallback to WebUSB
    if (isWebUSBAvailable()) {
      const connection = await checkDeviceConnection();
      return {
        success: true,
        available: connection.connected,
        method: 'webusb',
        message: connection.connected 
          ? 'Device connected via WebUSB' 
          : 'Device not found. Please connect Mantra MFS110 scanner.'
      };
    }

    // Check bridge service via proxy (to avoid CORS)
    const proxyUrl = '/rdservice';
    try {
      const response = await fetch(`${proxyUrl}/status`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        signal: AbortSignal.timeout(3000)
      });
      if (response.ok) {
        return {
          success: true,
          available: true,
          method: 'bridge',
          message: 'Bridge service available via proxy',
          url: proxyUrl
        };
      }
    } catch (e) {
      // Bridge service not available via proxy
      console.log('[getDeviceStatus] Bridge service check via proxy failed:', e.message);
    }
    
    return {
      success: false,
      available: false,
      method: 'none',
      message: 'RD Service not found. Please ensure Mantra RD Service is running and accessible.'
    };
  } catch (error) {
    return {
      success: false,
      available: false,
      error: error.message,
      message: 'Error checking device status: ' + error.message
    };
  }
};

// Test RD Service connection (for debugging)
const testRDServiceConnection = async (url = null) => {
  // Try proxy first to avoid CORS
  const testUrls = [
    '/rdservice', // Proxy URL (recommended)
    url || import.meta.env.VITE_RD_SERVICE_URL || 'http://localhost:8080'
  ].filter(Boolean);
  
  for (const testUrl of testUrls) {
    console.log('Testing RD Service connection to:', testUrl);
    
    try {
      const response = await fetch(testUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit'
      });
      
      console.log('Test response status:', response.status);
      console.log('Test response headers:', [...response.headers.entries()]);
      
      return {
        success: true,
        status: response.status,
        url: testUrl,
        message: 'Connection successful',
        method: testUrl.startsWith('/') ? 'proxy' : 'direct'
      };
    } catch (error) {
      console.error(`Test connection failed for ${testUrl}:`, error);
      // Try next URL
      continue;
    }
  }
  
  return {
    success: false,
    error: 'All connection attempts failed. Check if RD Service is running and proxy is configured.',
    urls: testUrls
  };
};

export default {
  captureFingerprint,
  enrollFingerprint,
  verifyFingerprint,
  checkEnrollment,
  getDeviceStatus,
  isWebUSBAvailable,
  testRDServiceConnection,
  checkRDService // Export for testing
};

